import paper from 'paper';

import { Point2D } from '@paragon/contract-library';
import { hypotenuse2D } from '@paragon/utilities';

import { RenderedItem } from './paperItemTranslator';

// The desired grip size in screen pixels.
const GRIP_SIZE = 30;

export type CornerGripName = 'topLeft' | 'topRight' | 'bottomRight' | 'bottomLeft';
export type SideGripName = 'topCenter' | 'rightCenter' | 'bottomCenter' | 'leftCenter';
export type GripName = CornerGripName | SideGripName;

const CORNERS = ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'];

const OPPOSITE_CORNERS: { readonly [K in CornerGripName]: CornerGripName } = {
  topRight: 'bottomLeft',
  bottomRight: 'topLeft',
  topLeft: 'bottomRight',
  bottomLeft: 'topRight',
};

const OPPOSITE_SIDES: { readonly [K in SideGripName]: SideGripName } = {
  topCenter: 'bottomCenter',
  rightCenter: 'leftCenter',
  bottomCenter: 'topCenter',
  leftCenter: 'rightCenter',
};

export function isCorner(gripName: GripName): gripName is CornerGripName {
  return CORNERS.includes(gripName);
}

export function isVertical(sideName: SideGripName) {
  return sideName === 'rightCenter' || sideName === 'leftCenter';
}

export function getOppositeCorner(cornerName: CornerGripName) {
  return OPPOSITE_CORNERS[cornerName];
}

export function getOppositeSide(sideName: SideGripName) {
  return OPPOSITE_SIDES[sideName];
}

export function getGripUnderPoint(point: paper.Point, item: RenderedItem) {
  if (!item.annotationIsEditableByCurrentUser) {
    return null;
  }

  const gripPositions = getAnnotationGrips(item);
  const gripAreasOnItem = Object.keys(gripPositions);
  const gripSize = getGripSize(item);

  return (gripAreasOnItem as readonly GripName[]).find((area) => isPointOnGrip(point, gripPositions[area]!, gripSize));
}

function pointFromSegment(segment: paper.Segment) {
  return {
    x: segment.point.x,
    y: segment.point.y,
  };
}

type GripPoints = {
  readonly [K in GripName]?: Point2D;
};

function getAnnotationGripsForPath(item: paper.Path) {
  // Complex paths are not editable yet
  if (item.segments.length !== 2) {
    return {};
  }

  const points = item.segments.map(pointFromSegment);

  const xChange = Math.abs(points[0].x - points[1].x);
  const yChange = Math.abs(points[0].y - points[1].y);

  const lineOrientation = xChange > yChange ? 'horizontal' : 'vertical';

  switch (lineOrientation) {
    case 'vertical': {
      const topPoint = points[0].y > points[1].y ? 0 : 1;
      return {
        topCenter: points[topPoint],
        bottomCenter: points[opposite(topPoint)],
      };
    }

    case 'horizontal': {
      const firstPoint = points[0].x < points[1].x ? 0 : 1;
      return {
        leftCenter: points[firstPoint],
        rightCenter: points[opposite(firstPoint)],
      };
    }

    default:
      throw new Error(`Invalid line orientation: ${lineOrientation}`);
  }
}

export function getAnnotationGrips(item: RenderedItem): GripPoints {
  if (item instanceof paper.Path) {
    return getAnnotationGripsForPath(item);
  }

  const halfHeight = (item.internalBounds.height / 2) * item.scaling.y;
  const halfWidth = (item.internalBounds.width / 2) * item.scaling.x;
  const { center } = item.bounds;
  const distanceToCorner = hypotenuse2D(halfHeight, halfWidth);
  const angleToCorner = Math.atan(halfHeight / halfWidth);
  let angle = item.rotation;
  if (angle > 0) {
    angle = -180 - (180 - item.rotation);
  }
  const rotationAngleInRadians = -angle * (Math.PI / 180);
  const angleToTopLeftCornerFromNegativeXAxisAfterRotation = angleToCorner - rotationAngleInRadians;
  const angleToTopRightCornerFromPositiveXAxisAfterRotation = angleToCorner + rotationAngleInRadians;

  // Find the x and y distances to left center, top left, top center, and top right
  // These values will be used in relation to the center of the annotation
  // Mirror these distances across the center to find the right center, bottom right, bottom center,
  // and bottom left respectively
  const xDistanceToTopCenter = Math.sin(rotationAngleInRadians) * halfHeight;
  const yDistanceToTopCenter = Math.cos(rotationAngleInRadians) * halfHeight;
  const xDistanceToLeftCenter = Math.cos(rotationAngleInRadians) * halfWidth;
  const yDistanceToLeftCenter = Math.sin(rotationAngleInRadians) * halfWidth;
  const xDistanceToTopLeft = Math.cos(angleToTopLeftCornerFromNegativeXAxisAfterRotation) * distanceToCorner;
  const yDistanceToTopLeft = Math.sin(angleToTopLeftCornerFromNegativeXAxisAfterRotation) * distanceToCorner;
  const xDistanceToTopRight = Math.cos(angleToTopRightCornerFromPositiveXAxisAfterRotation) * distanceToCorner;
  const yDistanceToTopRight = Math.sin(angleToTopRightCornerFromPositiveXAxisAfterRotation) * distanceToCorner;

  const annotationPointsOfInterest = {
    topLeft: {
      x: center.x - xDistanceToTopLeft,
      y: center.y - yDistanceToTopLeft,
    },
    topRight: {
      x: center.x + xDistanceToTopRight,
      y: center.y - yDistanceToTopRight,
    },
    bottomRight: {
      x: center.x + xDistanceToTopLeft,
      y: center.y + yDistanceToTopLeft,
    },
    bottomLeft: {
      x: center.x - xDistanceToTopRight,
      y: center.y + yDistanceToTopRight,
    },
    topCenter: {
      x: center.x - xDistanceToTopCenter,
      y: center.y - yDistanceToTopCenter,
    },
    rightCenter: {
      x: center.x + xDistanceToLeftCenter,
      y: center.y - yDistanceToLeftCenter,
    },
    bottomCenter: {
      x: center.x + xDistanceToTopCenter,
      y: center.y + yDistanceToTopCenter,
    },
    leftCenter: {
      x: center.x - xDistanceToLeftCenter,
      y: center.y + yDistanceToLeftCenter,
    },
  };

  return rotateGrips(annotationPointsOfInterest, rotationAngleInRadians);
}

type GripSize = {
  readonly width: number;
  readonly height: number;
};

function getGripSize(item: RenderedItem): GripSize {
  const standardSize = GRIP_SIZE / paper.view.zoom;

  // Paths are a special case because their bounds are only a stroke wide in one dimension. We cap both dimensions by
  //   half the distance between the endpoints instead to ensure the two grips can never overlap.
  if (item instanceof paper.Path && item.segments.length === 2) {
    const [start, end] = item.segments;
    const size = Math.min(standardSize, start.point.getDistance(end.point) / 2);
    return { width: size, height: size };
  }

  return {
    width: Math.min(standardSize, item.bounds.width / 2),
    height: Math.min(standardSize, item.bounds.height / 2),
  };
}

function isPointOnGrip(point: paper.Point, gripPoint: Point2D, gripSize: GripSize) {
  return Math.abs(point.x - gripPoint.x) < gripSize.width / 2 && Math.abs(point.y - gripPoint.y) < gripSize.height / 2;
}

function getNumberOfSwaps(angle: number) {
  if (angle >= 45 && angle < 135) {
    return 1;
  }
  if (angle >= 135 && angle < 225) {
    return 2;
  }
  if (angle >= 225 && angle < 315) {
    return 3;
  }

  return 0;
}

function rotateGrips(grips: GripPoints, rotationAngleInRadians: number) {
  const corners = [grips.topLeft, grips.topRight, grips.bottomRight, grips.bottomLeft];

  const sides = [grips.topCenter, grips.rightCenter, grips.bottomCenter, grips.leftCenter];

  const rotationAngle = rotationAngleInRadians * (180 / Math.PI);
  const numberOfSwaps = getNumberOfSwaps(rotationAngle);
  return {
    topLeft: corners[numberOfSwaps],
    topCenter: sides[numberOfSwaps],
    topRight: corners[(1 + numberOfSwaps) % 4],
    rightCenter: sides[(1 + numberOfSwaps) % 4],
    bottomRight: corners[(2 + numberOfSwaps) % 4],
    bottomCenter: sides[(2 + numberOfSwaps) % 4],
    bottomLeft: corners[(3 + numberOfSwaps) % 4],
    leftCenter: sides[(3 + numberOfSwaps) % 4],
  };
}

// 1 => 0; 0 => 1
function opposite(n: 0 | 1) {
  return n === 0 ? 1 : 0;
}
